/**
 * ⛔⛔ THE ONE PLACE THE UI LEARNS WHAT VERSION IT IS — Card P131-VER.
 *
 * MEASURED 26 Oct 2026, before this card: the product carried THREE
 * disagreeing version strings and no way to tell which deploy you were
 * looking at.
 *   · `2.0.0`  — package.json, preload.js, public/apiClient.js,
 *                Sidebar.js and LoginPage.js (TWICE)
 *   · `2.4.4`  — server.js's /health probe, three separate literals
 *   · `v1.0.2` — the newest git tag
 * Six copies of one number in the client alone, which is why every one of
 * them was still "2.0.0" months after 2.0.0 shipped: nobody edits six places.
 *
 * ⚠ So this module exports a VALUE, not a constant to copy. The rule that
 * keeps it true is tests/react/versionSingleSource.test.js, which forbids a
 * version-shaped literal anywhere else in src/.
 *
 * ── WHERE THE VALUES COME FROM ────────────────────────────────────────────
 * `npm run build` inlines REACT_APP_* at BUILD time (create-react-app
 * substitutes them textually; there is no process.env at runtime in the
 * browser). deploy.yml's deploy-frontend job sets them from the version it
 * computed for this deploy.
 *
 * ⛔⛔ THE FALLBACK IS NOT A VERSION NUMBER, AND THAT IS DELIBERATE. An
 * unbuilt or mis-built bundle reads "dev", never a plausible-looking "2.1.0".
 * A wrong number that looks right is the failure this card exists to end: it
 * is worse than an obvious blank, because it is quietly trusted in a bug
 * report. Three states — a real version, "dev", never a guess.
 */

/** The semantic version of this build, or "dev" when it was not injected. */
export const APP_VERSION = process.env.REACT_APP_VERSION || "dev";

/** Short commit sha of the build, or "" when unknown. */
export const APP_COMMIT = process.env.REACT_APP_COMMIT || "";

/** ISO-8601 build timestamp, or "" when unknown. */
export const APP_BUILT_AT = process.env.REACT_APP_BUILT_AT || "";

/** True when this bundle carries a real injected version. */
export const IS_VERSIONED = APP_VERSION !== "dev";

/**
 * The date half of the build stamp, as YYYY-MM-DD.
 *
 * ⚠ SLICED, NOT FORMATTED WITH toLocaleDateString. A locale-formatted date
 * renders differently for different viewers, so two people reading the same
 * footer would report different builds to each other. The build stamp is an
 * identifier; it is the same characters for everyone.
 */
export function buildDate(builtAt = APP_BUILT_AT) {
  const s = String(builtAt || "");
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(s);
  return m ? m[1] : "";
}

/**
 * The footer string: "v2.1.0 · a1b2c3d · 2026-10-26".
 *
 * ⚠ Each piece is dropped if absent rather than rendered as "undefined" or an
 * empty segment with stray separators — a half-known build still has to read
 * as a sentence.
 */
export function versionLine({
  version = APP_VERSION, commit = APP_COMMIT, builtAt = APP_BUILT_AT,
} = {}) {
  const parts = [`v${version}`];
  if (commit) parts.push(String(commit));
  const d = buildDate(builtAt);
  if (d) parts.push(d);
  return parts.join(" · ");
}
